import React, { useState, useEffect } from 'react';
import { Flame } from 'lucide-react';
import { SEO } from '../components/SEO';

interface BBQState {
  adultGuests: string;
  childGuests: string;
  meatType: 'mixed' | 'beef' | 'pork' | 'chicken' | 'fish';
  includeSides: boolean;
  includeDrinks: boolean;
  includeVegetarian: boolean;
}

interface BBQResult {
  totalMeat: number;
  charcoal: number;
  bread: number;
  sides: {
    salad: number;
    potatoes: number;
    vegetables: number;
  };
  drinks: {
    water: number;
    beer: number;
    soda: number;
  };
  vegetarianOptions: number;
  estimatedCost: number;
}

function KalkulatorRostilja() {
  const [values, setValues] = useState<BBQState>({
    adultGuests: '',
    childGuests: '',
    meatType: 'mixed',
    includeSides: true,
    includeDrinks: true,
    includeVegetarian: false
  });

  const [results, setResults] = useState<BBQResult | null>(null);

  const calculateBBQ = () => {
    const adults = parseInt(values.adultGuests) || 0;
    const children = parseInt(values.childGuests) || 0;
    
    if (adults === 0 && children === 0) {
      return null;
    }

    // Calculate meat per person (in grams)
    let meatPerAdult = 0;
    let meatPerChild = 0;

    switch (values.meatType) {
      case 'beef':
        meatPerAdult = 300;
        meatPerChild = 150;
        break;
      case 'pork':
        meatPerAdult = 350;
        meatPerChild = 175;
        break;
      case 'chicken':
        meatPerAdult = 250;
        meatPerChild = 125;
        break;
      case 'fish':
        meatPerAdult = 200;
        meatPerChild = 100;
        break;
      case 'mixed':
      default:
        meatPerAdult = 300;
        meatPerChild = 150;
        break;
    }

    // Calculate total meat (in kg)
    const totalMeat = ((adults * meatPerAdult) + (children * meatPerChild)) / 1000;
    
    // Calculate charcoal (in kg) - typically 1.5kg per kg of meat
    const charcoal = totalMeat * 1.5;
    
    // Calculate bread (in kg) - typically 100g per person
    const bread = ((adults * 100) + (children * 50)) / 1000;
    
    // Calculate sides
    const sides = {
      salad: ((adults * 150) + (children * 75)) / 1000, // in kg
      potatoes: ((adults * 200) + (children * 100)) / 1000, // in kg
      vegetables: ((adults * 150) + (children * 75)) / 1000 // in kg
    };
    
    // Calculate drinks
    const drinks = {
      water: (adults + children) * 0.5, // in liters
      beer: adults * 0.75, // in liters
      soda: ((adults * 0.5) + (children * 0.3)) // in liters
    };
    
    // Calculate vegetarian options (in kg) if needed
    const vegetarianOptions = values.includeVegetarian ? 
      ((adults + children) * 0.2 * 150) / 1000 : 0; // assuming 20% might prefer vegetarian
    
    // Estimate cost (rough estimate in EUR)
    let estimatedCost = totalMeat * 10; // Meat cost
    estimatedCost += charcoal * 2; // Charcoal cost
    estimatedCost += bread * 3; // Bread cost
    
    if (values.includeSides) {
      estimatedCost += (sides.salad * 5) + (sides.potatoes * 2) + (sides.vegetables * 4);
    }
    
    if (values.includeDrinks) {
      estimatedCost += (drinks.water * 1) + (drinks.beer * 3) + (drinks.soda * 2);
    }
    
    if (values.includeVegetarian) {
      estimatedCost += vegetarianOptions * 8;
    }
    
    return {
      totalMeat,
      charcoal,
      bread,
      sides,
      drinks,
      vegetarianOptions,
      estimatedCost: parseFloat(estimatedCost.toFixed(2))
    };
  };

  const handleInputChange = (field: keyof BBQState, value: string | boolean) => {
    setValues(prev => ({
      ...prev,
      [field]: value
    }));
  };

  useEffect(() => {
    if (values.adultGuests || values.childGuests) {
      const results = calculateBBQ();
      setResults(results);
    }
  }, [values]);

  const formatNumber = (num: number): string => {
    return new Intl.NumberFormat('hr-HR', {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1
    }).format(num);
  };

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "Kalkulator Roštilja",
    "applicationCategory": "CalculatorApplication",
    "operatingSystem": "Any",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "EUR"
    },
    "description": "Izračunajte potrebne količine namirnica za roštilj. Uključuje meso, ugljen, piće i priloge za savršen roštilj.",
    "url": "https://online-kalkulator.com/kalkulator-rostilja",
    "inLanguage": "hr",
    "author": {
      "@type": "Organization",
      "name": "Online Kalkulator"
    },
    "featureList": [
      "Izračun količine mesa",
      "Izračun potrebnog ugljena",
      "Izračun pića i priloga",
      "Procjena troškova",
      "Besplatno korištenje"
    ]
  };

  return (
    <>
      <SEO 
        title="Kalkulator Roštilja | Izračun Količine Mesa i Namirnica za Roštilj"
        description="Izračunajte potrebne količine namirnica za roštilj. Uključuje meso, ugljen, piće i priloge za savršen roštilj."
        canonicalUrl="/kalkulator-rostilja"
        structuredData={structuredData}
      />
      <div className="max-w-4xl mx-auto px-4">
        <div className="flex items-center justify-center gap-3 mb-6">
          <Flame className="w-8 h-8 text-orange-500" />
          <h1 className="text-3xl font-bold text-gray-800 text-center">Kalkulator Roštilja</h1>
        </div>

        <div className="max-w-2xl mx-auto">
          {/* Calculator Section */}
          <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
            <div className="space-y-6">
              {/* Input Fields */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Broj odraslih osoba
                  </label>
                  <input
                    type="number"
                    value={values.adultGuests}
                    onChange={(e) => handleInputChange('adultGuests', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    placeholder="npr. 6"
                    min="0"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Broj djece
                  </label>
                  <input
                    type="number"
                    value={values.childGuests}
                    onChange={(e) => handleInputChange('childGuests', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    placeholder="npr. 3"
                    min="0"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Vrsta mesa
                </label>
                <select
                  value={values.meatType}
                  onChange={(e) => handleInputChange('meatType', e.target.value as BBQState['meatType'])}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                >
                  <option value="mixed">Miješano meso</option>
                  <option value="beef">Govedina</option>
                  <option value="pork">Svinjetina</option>
                  <option value="chicken">Piletina</option>
                  <option value="fish">Riba</option>
                </select>
              </div>

              <div className="space-y-2">
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={values.includeSides}
                    onChange={(e) => handleInputChange('includeSides', e.target.checked)}
                    className="w-4 h-4 text-orange-500 rounded focus:ring-orange-500"
                  />
                  <span className="text-sm text-gray-700">Uključi priloge (salata, krumpir, povrće)</span>
                </label>

                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={values.includeDrinks}
                    onChange={(e) => handleInputChange('includeDrinks', e.target.checked)}
                    className="w-4 h-4 text-orange-500 rounded focus:ring-orange-500"
                  />
                  <span className="text-sm text-gray-700">Uključi piće (voda, pivo, sokovi)</span>
                </label>

                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={values.includeVegetarian}
                    onChange={(e) => handleInputChange('includeVegetarian', e.target.checked)}
                    className="w-4 h-4 text-orange-500 rounded focus:ring-orange-500"
                  />
                  <span className="text-sm text-gray-700">Uključi vegetarijanske opcije</span>
                </label>
              </div>

              {/* Results */}
              {results && (
                <div className="space-y-4 border-t pt-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-orange-50 p-4 rounded-lg text-center">
                      <div className="text-gray-600 text-sm mb-1">Ukupno mesa</div>
                      <div className="text-xl font-bold text-orange-600">{formatNumber(results.totalMeat)} kg</div>
                    </div>
                    <div className="bg-orange-50 p-4 rounded-lg text-center">
                      <div className="text-gray-600 text-sm mb-1">Ugljen</div>
                      <div className="text-xl font-bold text-orange-600">{formatNumber(results.charcoal)} kg</div>
                    </div>
                  </div>

                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h3 className="font-medium text-gray-700 mb-2">Dodatne namirnice:</h3>
                    <div className="grid grid-cols-2 gap-2">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Kruh:</span>
                        <span className="font-medium">{formatNumber(results.bread)} kg</span>
                      </div>
                      
                      {values.includeSides && (
                        <>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Salata:</span>
                            <span className="font-medium">{formatNumber(results.sides.salad)} kg</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Krumpir:</span>
                            <span className="font-medium">{formatNumber(results.sides.potatoes)} kg</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Povrće:</span>
                            <span className="font-medium">{formatNumber(results.sides.vegetables)} kg</span>
                          </div>
                        </>
                      )}
                      
                      {values.includeDrinks && (
                        <>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Voda:</span>
                            <span className="font-medium">{formatNumber(results.drinks.water)} L</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Pivo:</span>
                            <span className="font-medium">{formatNumber(results.drinks.beer)} L</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Sokovi:</span>
                            <span className="font-medium">{formatNumber(results.drinks.soda)} L</span>
                          </div>
                        </>
                      )}
                      
                      {values.includeVegetarian && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">Vegetarijanske opcije:</span>
                          <span className="font-medium">{formatNumber(results.vegetarianOptions)} kg</span>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex justify-between items-center pt-3 border-t">
                    <span className="text-gray-800 font-medium">Procijenjeni trošak:</span>
                    <span className="text-xl font-bold text-orange-600">
                      {formatNumber(results.estimatedCost)} €
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Information Section */}
          <div className="bg-white rounded-xl shadow-lg p-8">
            <div className="space-y-8">
              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">O Kalkulatoru Roštilja</h2>
                <p className="text-gray-600 leading-relaxed">
                  Kalkulator roštilja je koristan alat koji vam pomaže izračunati potrebne količine
                  namirnica za vaš roštilj. Bez obzira organizirate li obiteljsko okupljanje ili
                  veće druženje, ovaj kalkulator će vam pomoći da pravilno isplanirate količine
                  mesa, priloga i pića.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Kako Koristiti</h2>
                <ol className="list-decimal list-inside text-gray-600 space-y-2">
                  <li>Unesite broj odraslih osoba i djece</li>
                  <li>Odaberite vrstu mesa koju planirate pripremiti</li>
                  <li>Označite dodatne opcije prema potrebi:
                    <ul className="list-none pl-6 pt-2 space-y-1">
                      <li>- Prilozi (salata, krumpir, povrće)</li>
                      <li>- Piće (voda, pivo, sokovi)</li>
                      <li>- Vegetarijanske opcije</li>
                    </ul>
                  </li>
                  <li>Rezultati će se automatski izračunati</li>
                  <li>Koristite izračunate količine kao vodič za kupovinu</li>
                </ol>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Preporučene Količine</h2>
                <div className="space-y-4">
                  <div>
                    <h3 className="text-lg font-medium text-gray-700 mb-2">Meso po osobi</h3>
                    <ul className="list-disc list-inside text-gray-600 space-y-1">
                      <li>Odrasli: 250-350g mesa po osobi</li>
                      <li>Djeca: 125-175g mesa po osobi</li>
                      <li>Miješano meso: 300g po odrasloj osobi</li>
                      <li>Govedina: 300g po odrasloj osobi</li>
                      <li>Svinjetina: 350g po odrasloj osobi</li>
                      <li>Piletina: 250g po odrasloj osobi</li>
                      <li>Riba: 200g po odrasloj osobi</li>
                    </ul>
                  </div>
                  <div>
                    <h3 className="text-lg font-medium text-gray-700 mb-2">Ugljen i oprema</h3>
                    <ul className="list-disc list-inside text-gray-600 space-y-1">
                      <li>Ugljen: 1.5kg ugljena po 1kg mesa</li>
                      <li>Tekućina za potpalu: 100ml po roštilju</li>
                      <li>Aluminijska folija: 1 rola</li>
                      <li>Drveni štapići: 2-3 po osobi (za ćevape, ražnjiće)</li>
                    </ul>
                  </div>
                </div>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Savjeti za Uspješan Roštilj</h2>
                <ul className="list-disc list-inside text-gray-600 space-y-2">
                  <li>Pripremite meso dan ranije i marinirajte ga preko noći</li>
                  <li>Ugljen zapalite 30-45 minuta prije pečenja</li>
                  <li>Koristite različite temperature na roštilju za različite vrste mesa</li>
                  <li>Nemojte okretati meso prečesto</li>
                  <li>Ostavite meso da se "odmori" 5-10 minuta nakon pečenja</li>
                  <li>Pripremite sve priloge unaprijed</li>
                  <li>Osigurajte dovoljno sjedećih mjesta i pribora za jelo</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Vegetarijanske Opcije</h2>
                <ul className="list-disc list-inside text-gray-600 space-y-2">
                  <li>Povrće za roštilj (tikvice, paprike, gljive, kukuruz)</li>
                  <li>Halloumi sir ili tofu</li>
                  <li>Vegetarijanski burgeri ili kobasice</li>
                  <li>Punjene paprike ili patlidžani</li>
                  <li>Ražnjići od povrća</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Napomene</h2>
                <ul className="list-disc list-inside text-gray-600 space-y-2">
                  <li>Izračuni su informativne prirode</li>
                  <li>Stvarne potrebe mogu varirati ovisno o apetitu gostiju</li>
                  <li>Uvijek je bolje pripremiti malo više nego premalo</li>
                  <li>Procijenjeni trošak je okvirni i može varirati ovisno o cijenama u vašem području</li>
                  <li>Prilagodite količine prema vlastitom iskustvu i preferencijama</li>
                </ul>
              </section>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default KalkulatorRostilja;